import React from "react";
import { Link } from "react-router-dom";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import Tag from "../../../ui-components/molecules/tag/tag";
import { PlannableProbillsFreightStatus } from "../../../app/data/plan-probills/models";
import { TagColor } from "../../../app/data/common/tagColor";
import { Routes } from "../../../app/route/RoutesConfig";

/** Format a duration (ms) as human-readable e.g. "34 minutes", "1 hour", "2 days" */
const formatDuration = (ms: number): string => {
  const d = moment.duration(ms);
  const minutes = Math.floor(d.asMinutes());
  const hours = Math.floor(d.asHours());
  const days = Math.floor(d.asDays());
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
  if (minutes >= 1) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  return "less than a minute";
};

/** Format ISO 8601 timestamp as "X ago" (e.g. "1 hour ago", "34 minutes ago") */
const formatTimeAgo = (iso: string | undefined): string => {
  if (!iso) return "";
  const m = moment(iso);
  if (!m.isValid()) return "";
  const diffMs = moment().valueOf() - m.valueOf();
  return `${formatDuration(diffMs)} ago`;
};

/** Format time until/since a timestamp: "in X" (future) or "X ago" (past) */
const formatTimeUntilOrAgo = (iso: string | undefined): { text: string; isPast: boolean } => {
  if (!iso) return { text: "", isPast: false };
  const m = moment(iso);
  if (!m.isValid()) return { text: "", isPast: false };
  const diffMs = m.valueOf() - moment().valueOf();
  if (diffMs >= 0) return { text: formatDuration(diffMs), isPast: false };
  return { text: formatDuration(-diffMs), isPast: true };
};

const getFormattedDate = (date: number): string => {
  if (!date || !+date) return "";
  const m = moment(date.toString(), "YYYYMMDD");
  return m.isValid() ? m.format("MM/DD/YY") : "";
};

const getFormattedTime = (time: number) => {
  let timeStr = `000000${time}`.slice(-6);
  let hour = +timeStr.slice(0, 2);
  if (hour < 12) return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)} am`;
  else return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)} pm`;
}

/** Format ISO 8601 timestamp to local date and time */
const formatIsoToLocal = (iso: string | undefined): string => {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.local().format("MM/DD/YY h:mm A") : "";
};

/** Sort faceted [key, rowCount] pairs for select filters (keys are ISO datetimes). */
const sortFacetEntriesIsoKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const k1 = String(a[0] ?? "");
  const k2 = String(b[0] ?? "");
  const t1 = moment(k1).valueOf();
  const t2 = moment(k2).valueOf();
  const ok1 = Number.isFinite(t1);
  const ok2 = Number.isFinite(t2);
  if (!ok1 && !ok2) return k1.localeCompare(k2);
  if (!ok1) return 1;
  if (!ok2) return -1;
  return t1 - t2;
};

/** Sort faceted [key, rowCount] pairs for numeric manifest / trailer keys. */
const sortFacetEntriesNumericKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const k1 = String(a[0] ?? "");
  const k2 = String(b[0] ?? "");
  const n1 = k1 === "" ? NaN : Number(k1);
  const n2 = k2 === "" ? NaN : Number(k2);
  const ok1 = !Number.isNaN(n1);
  const ok2 = !Number.isNaN(n2);
  if (!ok1 && !ok2) return k1.localeCompare(k2);
  if (!ok1) return 1;
  if (!ok2) return -1;
  return n1 - n2;
};

const formatTrailerFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "") return "—";
  return `TL-${key}`;
};

/** Human-readable freight status for cells and filter options (enum → same labels as the column). */
const formatFreightStatusLabel = (
  value: PlannableProbillsFreightStatus | string | null | undefined
): string => {
  if (value === PlannableProbillsFreightStatus.AT_DOCK) return "At Dock";
  if (value === PlannableProbillsFreightStatus.INBOUND) return "Inbound";
  if (value != null && String(value).trim() !== "") return String(value);
  return "—";
};

const tagsMap: any = {
  "Appointment Needed": {
    icon: XGSIcons.faCalendarTimes,
    style: {
      color: "#C43232",
      backgroundColor: "#DD3F3F33"
    }
  },
  "Appointment Set": {
    icon: XGSIcons.faCalendarCheck,
    style: {
      color: "#14870A",
      backgroundColor: "#5DC75433"
    },
    formatDetails: (details: any) => {
      if (details && +details) return `(${getFormattedDate(details)})`;
      else return "";
    }
  },
  "Standing Appointment": {
    icon: XGSIcons.faCalendar,
    getStyles: (details: any) => {
      if (details && details.startTime && (details.startTime === details.endTime)) {
        return {
          color: "#C43232",
          backgroundColor: "#DD3F3F33"
        }
      } else return {
        color: "#14870A",
        backgroundColor: "#5DC75433"
      }
    },
    formatDetails: (details: any) => {
      if (details && details.startTime) {
        return `(${getFormattedTime(details.startTime)} - ${getFormattedTime(details.endTime)})`;
      }
      else return "";
    }
  },
  "Residential": {
    icon: XGSIcons.faHome,
    style: {
      color: "#B89311",
      backgroundColor: "#E8D15A33"
    }
  },
  "Driver Collect": {
    icon: XGSIcons.faDollarSign,
    style: {
      color: "#B89311",
      backgroundColor: "#E8D15A33"
    }
  }
}

/** Same rule as Accessorials: any tag with `type === "Appointment Needed"`. */
const rowHasAppointmentNeeded = (tagTypes: unknown): boolean => {
  if (!Array.isArray(tagTypes)) return false;
  return tagTypes.some(
    (tagObj: { type?: string }) => tagObj?.type === "Appointment Needed"
  );
};

const getAccessorialIcons = (tagTypes: any, probillNumber: number, onScheduleAppointment?: (probillNumber: number) => void) => {
  if (!tagTypes || !tagTypes.length) return null;
  return (
    <div className="plan-probills-accessorials-icons">
      {tagTypes.map((tagObj: any, tagIdx: number) => {
        const tagType = tagObj.type;
        const tagDetails = tagObj.details;
        if (!tagsMap[tagType]) return null;
        const styles = (tagsMap[tagType].getStyles) ? tagsMap[tagType].getStyles(tagDetails) : tagsMap[tagType].style;
        const icon = <XGSIcon icon={tagsMap[tagType].icon} />;
        const title = tagsMap[tagType].formatDetails
          ? `${tagType} ${tagsMap[tagType].formatDetails(tagDetails)}`
          : tagDetails ? `${tagType}: ${tagDetails}` : tagType;
        if (tagType === "Appointment Needed" && onScheduleAppointment) {
          return (
            <button
              key={`${tagIdx}_t`}
              type="button"
              className="plan-probills-accessorials-icon-btn"
              style={styles}
              title={title}
              onClick={(e) => {
                e.stopPropagation();
                onScheduleAppointment(probillNumber);
              }}
              aria-label={`Schedule appointment for probill ${probillNumber}`}
            >
              {icon}
            </button>
          );
        }
        return (
          <span
            key={`${tagIdx}_t`}
            className="plan-probills-accessorials-icon"
            style={styles}
            title={title}
          >
            {icon}
          </span>
        );
      })}
    </div>
  );
};
const getFormattedConsignee = (rawValue: string) => {
  if (rawValue) {
    let splitValues = rawValue.split("|");
    if (splitValues && splitValues.length === 2) {
      return <div>{splitValues[0]}{" ("}<span>
        {splitValues[1]}
      </span>{")"}</div>
    } else {
      return rawValue;
    }
  } else {
    return ""
  }
}

const formatNameWithNumber = (name?: string | null, number?: number | null): string => {
  if (!name && number == null) return "—";
  if (!name) return String(number);
  if (number == null) return name;
  return `${name} (${number})`;
};

const formatPartyAddress = (
  street?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: number | string | null
): string => {
  const zipStr = zip != null ? String(zip).trim() : "";
  if (street && city && state) {
    return `${street}, ${city}, ${state}${zipStr ? ` ${zipStr}` : ""}`;
  }
  const parts = [street, city, state, zipStr].filter((part) => part != null && String(part).trim() !== "");
  return parts.length ? parts.join(", ") : "—";
};

const formatAttention = (value?: string | null): string => {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
};

const columnHelper = createColumnHelper<any>();

/** Column id and label for column visibility selector */
export const PLAN_PROBILLS_COLUMN_OPTIONS: { id: string; label: string }[] = [
  { id: "selection", label: "Selection" },
  { id: "probillNumber", label: "Probill" },
  { id: "staticRouteName", label: "Static Route" },
  { id: "location", label: "Location" },
  { id: "etaDate", label: "ETA" },
  { id: "freightStatus", label: "Freight Status" },
  { id: "inboundManifestNumber", label: "Manifest" },
  { id: "inboundTrailerNumber", label: "Trailer" },
  { id: "arrivedAt", label: "Arrived" },
  { id: "expectedArrival", label: "Expected Arrival" },
  { id: "originTerminal", label: "Origin Terminal" },
  { id: "shipper", label: "Shipper" },
  { id: "shipperAddress", label: "Shipper Address" },
  { id: "shipperAttention", label: "Shipper Attention" },
  { id: "payor", label: "Payor" },
  { id: "payorAddress", label: "Payor Address" },
  { id: "payorAttention", label: "Payor Attention" },
  { id: "consignee", label: "Consignee" },
  { id: "consigneeAddress", label: "Street Address" },
  { id: "consigneeCity", label: "City" },
  { id: "consigneeZip", label: "Zip Code" },
  { id: "probillItems", label: "Pieces" },
  { id: "probillWeight", label: "Weight" },
  { id: "probillStatus", label: "Status" },
  { id: "tags", label: "Accessorials" },
];

export const PLAN_PROBILLS_COLUMNS_HIDDEN_BY_DEFAULT: string[] = [
  "freightStatus",
  "inboundManifestNumber",
  "inboundTrailerNumber",
  "arrivedAt",
  "expectedArrival",
  "originTerminal",
  "shipper",
  "shipperAddress",
  "shipperAttention",
  "payor",
  "payorAddress",
  "payorAttention",
];

export type PlanProbillsColumnsOptions = {
  onScheduleAppointment?: (probillNumber: number) => void;
  terminalNumber?: number | null;
};

const getPlanProbillsColumns = (options?: PlanProbillsColumnsOptions) => [
    columnHelper.accessor("probillNumber", {
      id: "probillNumber",
      header: "Probill",
      cell: (cellProps) => (
        <Link
          className="xgs-table-link-cell"
          to={`/shipments/${cellProps.getValue()}`}
          target={"_blank"}
          rel={"noopener noreferrer"}
        >
          {cellProps.getValue()}
        </Link>
      ),
      filterFn: "includesString",
      size: 100,
    }),
    columnHelper.accessor("staticRouteName", {
      id: "staticRouteName",
      header: "Static Route",
      meta: {
        filterVariant: "select",
      },
      size: 140,
    }),
    columnHelper.accessor("location", {
      id: "location",
      header: "Location",
      cell: (cellProps) => {
        const row = cellProps.row.original;
        const location = cellProps.getValue() as string;
        const freightStatus = row.freightStatus as PlannableProbillsFreightStatus | undefined;
        const inboundManifestNumber = row.inboundManifestNumber;
        const arrivedAt = row.arrivedAt as string | undefined;
        const expectedArrival = row.expectedArrival as string | undefined;
        const terminalNumber = options?.terminalNumber;

        const locationDisplay = location ?? "—";

        const row1 = freightStatus === PlannableProbillsFreightStatus.INBOUND &&
          inboundManifestNumber != null &&
          terminalNumber != null ? (
          <Link
            className="xgs-table-link-cell plan-probills-location-cell__link"
            to={`/${terminalNumber}${Routes.freights.inbound}?manifestNumber=${encodeURIComponent(String(inboundManifestNumber))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {locationDisplay}
          </Link>
        ) : (
          <span className="plan-probills-location-cell__link">{locationDisplay}</span>
        );

        let row2: React.ReactNode = null;

        if (freightStatus === PlannableProbillsFreightStatus.AT_DOCK) {
          const ago = arrivedAt ? formatTimeAgo(arrivedAt) : "";
          row2 = (
            <Tag mods={{ color: TagColor.GREEN, flex: true }} mix="plan-probills-location-meta-tag">
              <XGSIcon icon={XGSIcons.faWarehouse} />
              <span>{ago ? `At Dock, ${ago}` : "At Dock"}</span>
            </Tag>
          );
        } else if (freightStatus === PlannableProbillsFreightStatus.INBOUND) {
          if (arrivedAt) {
            const ago = formatTimeAgo(arrivedAt);
            row2 = (
              <Tag mods={{ color: TagColor.YELLOW, flex: true }} mix="plan-probills-location-meta-tag">
                <XGSIcon icon={XGSIcons.faWarehouse} />
                <span>At Yard, {ago}</span>
              </Tag>
            );
          } else if (expectedArrival) {
            const { text, isPast } = formatTimeUntilOrAgo(expectedArrival);
            if (!text) {
              row2 = null;
            } else if (isPast) {
              row2 = (
                <Tag mods={{ color: TagColor.RED, flex: true }} mix="plan-probills-location-meta-tag">
                  <XGSIcon icon={XGSIcons.faExclamationTriangle} />
                  <span>Arriving Late by {text}</span>
                </Tag>
              );
            } else {
              row2 = (
                <Tag mods={{ color: TagColor.BLUE, flex: true }} mix="plan-probills-location-meta-tag">
                  <XGSIcon icon={XGSIcons.faTruck} />
                  <span>Arriving in {text}</span>
                </Tag>
              );
            }
          }
        }

        return (
          <div className="plan-probills-location-cell">
            <div className="plan-probills-location-cell__row1">{row1}</div>
            {row2 != null && <div className="plan-probills-location-cell__row2">{row2}</div>}
          </div>
        );
      },
      meta: {
        filterVariant: "select",
      },
      size: 160,
    }),
    columnHelper.accessor("freightStatus", {
      id: "freightStatus",
      header: "Freight Status",
      cell: (cellProps) => {
        const value = cellProps.getValue() as PlannableProbillsFreightStatus | string | undefined;
        const label = formatFreightStatusLabel(value);
        const color = value === PlannableProbillsFreightStatus.INBOUND
          ? TagColor.BLUE
          : value === PlannableProbillsFreightStatus.AT_DOCK
            ? TagColor.GREEN
            : TagColor.GREY;
        return (
          <Tag mods={{ color }}>
            {label}
          </Tag>
        );
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatFreightStatusLabel,
      },
      size: 120,
    }),
    columnHelper.accessor("inboundManifestNumber", {
      id: "inboundManifestNumber",
      header: "Manifest",
      cell: (cellProps) => {
        const manifestNumber = cellProps.getValue() as number | undefined;
        const terminalNumber = options?.terminalNumber;
        if (manifestNumber == null) return "—";
        if (terminalNumber == null) return String(manifestNumber);
        return (
          <Link
            className="xgs-table-link-cell"
            to={`/${terminalNumber}${Routes.freights.inbound}?manifestNumber=${encodeURIComponent(String(manifestNumber))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {manifestNumber}
          </Link>
        );
      },
      meta: {
        filterVariant: "select",
        filterSortFn: sortFacetEntriesNumericKey,
      },
      filterFn: "weakEquals",
      size: 120,
    }),
    columnHelper.accessor("inboundTrailerNumber", {
      id: "inboundTrailerNumber",
      header: "Trailer",
      cell: (cellProps) => {
        const trailerNumber = cellProps.getValue() as number | undefined;
        const row = cellProps.row.original;
        const manifestNumber = row.inboundManifestNumber;
        const terminalNumber = options?.terminalNumber;
        const displayValue = trailerNumber != null ? `TL-${trailerNumber}` : "—";
        if (manifestNumber == null || terminalNumber == null) return displayValue;
        return (
          <Link
            className="xgs-table-link-cell"
            to={`/${terminalNumber}${Routes.freights.inbound}?manifestNumber=${encodeURIComponent(String(manifestNumber))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayValue}
          </Link>
        );
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatTrailerFilterLabel,
        filterSortFn: sortFacetEntriesNumericKey,
      },
      filterFn: "weakEquals",
      size: 120,
    }),
    columnHelper.accessor("arrivedAt", {
      id: "arrivedAt",
      header: "Arrived",
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()),
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatIsoToLocal,
        filterSortFn: sortFacetEntriesIsoKey,
      },
      filterFn: "equals",
      size: 150,
    }),
    columnHelper.accessor("expectedArrival", {
      id: "expectedArrival",
      header: "Expected Arrival",
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()),
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatIsoToLocal,
        filterSortFn: sortFacetEntriesIsoKey,
      },
      filterFn: "equals",
      size: 150,
    }),
    columnHelper.accessor(
      (row: any) => {
        const { originTerminalNumber, originTerminalName, originTerminalAirCode } = row;
        const parts = [
          originTerminalNumber != null ? String(originTerminalNumber) : "",
          originTerminalName || "",
          originTerminalAirCode ? `(${originTerminalAirCode})` : "",
        ].filter(Boolean);
        return parts.length ? parts.join(" ") : "";
      },
      {
        id: "originTerminal",
        header: "Origin Terminal",
        cell: ({ row }: any) => {
          const { originTerminalNumber, originTerminalName, originTerminalAirCode } = row.original;
          const parts = [
            originTerminalNumber != null ? String(originTerminalNumber) : "",
            originTerminalName || "",
            originTerminalAirCode ? `(${originTerminalAirCode})` : "",
          ].filter(Boolean);
          return parts.length ? parts.join(" ") : "—";
        },
        meta: {
          filterVariant: "select",
        },
        filterFn: "equalsString",
        size: 180,
      }
    ),
    columnHelper.accessor(
      (row: any) => formatNameWithNumber(row.shipperName, row.shipperNumber),
      {
        id: "shipper",
        header: "Shipper",
        cell: ({ row }: any) => formatNameWithNumber(row.original.shipperName, row.original.shipperNumber),
        meta: {
          filterVariant: "select",
        },
        filterFn: "equalsString",
        size: 180,
      }
    ),
    columnHelper.accessor(
      (row: any) =>
        formatPartyAddress(
          row.shipperStreetAddress,
          row.shipperCity,
          row.shipperState,
          row.shipperZipcode
        ),
      {
        id: "shipperAddress",
        header: "Shipper Address",
        cell: ({ row }: any) =>
          formatPartyAddress(
            row.original.shipperStreetAddress,
            row.original.shipperCity,
            row.original.shipperState,
            row.original.shipperZipcode
          ),
        filterFn: "includesString",
        size: 220,
      }
    ),
    columnHelper.accessor("shipperAttention", {
      id: "shipperAttention",
      header: "Shipper Attention",
      cell: (cellProps) => formatAttention(cellProps.getValue()),
      filterFn: "includesString",
      size: 140,
    }),
    columnHelper.accessor(
      (row: any) => formatNameWithNumber(row.payorName, row.payorNumber),
      {
        id: "payor",
        header: "Payor",
        cell: ({ row }: any) => formatNameWithNumber(row.original.payorName, row.original.payorNumber),
        meta: {
          filterVariant: "select",
        },
        filterFn: "equalsString",
        size: 160,
      }
    ),
    columnHelper.accessor(
      (row: any) =>
        formatPartyAddress(
          row.payorStreetAddress,
          row.payorCity,
          row.payorState,
          row.payorZipcode
        ),
      {
        id: "payorAddress",
        header: "Payor Address",
        cell: ({ row }: any) =>
          formatPartyAddress(
            row.original.payorStreetAddress,
            row.original.payorCity,
            row.original.payorState,
            row.original.payorZipcode
          ),
        filterFn: "includesString",
        size: 220,
      }
    ),
    columnHelper.accessor("payorAttention", {
      id: "payorAttention",
      header: "Payor Attention",
      cell: (cellProps) => formatAttention(cellProps.getValue()),
      filterFn: "includesString",
      size: 140,
    }),
    columnHelper.accessor("consignee", {
      id: "consignee",
      header: "Consignee",
      cell: (cellProps) => (
        <div>{getFormattedConsignee(cellProps.getValue())}</div>
      ),
      size: 150,
    }),
    columnHelper.accessor("consigneeAddress", {
      id: "consigneeAddress",
      header: "Street Address",
      size: 180,
    }),
    columnHelper.accessor("consigneeCity", {
      id: "consigneeCity",
      header: "City",
      meta: {
        filterVariant: "select",
      },
      size: 100,
    }),
    // columnHelper.accessor("consigneeState", {
    //   id: "consigneeState",
    //   header: "State",    
    //   meta: {
    //     filterVariant: "select",
    //   },
    //   size: 60,
    // }),
    columnHelper.accessor("consigneeZip", {
      id: "consigneeZip",
      header: "Zip Code",
      meta: {
        filterVariant: "select",
      },
      filterFn: "equals",
      size: 120,
    }),
    columnHelper.accessor("probillItems", {
      id: "probillItems",
      header: "Pieces",
      size: 100,
    }),
    columnHelper.accessor("probillWeight", {
      id: "probillWeight",
      header: "Weight",
      cell: (cellProps) => (
        <>
          {cellProps.getValue().toLocaleString('en', { useGrouping: true })}
        </>
      ),
      size: 130,
    }),
    columnHelper.accessor("etaDate", {
      id: "etaDate",
      header: "ETA",
      cell: (cellProps) => {
        const row = cellProps.row.original;
        const etaDate = cellProps.getValue() as number | undefined;
        const appointmentNeeded = rowHasAppointmentNeeded(row.tags);
        const onSchedule = options?.onScheduleAppointment;
        const canCreateAppointment = onSchedule != null;

        const formatted =
          etaDate != null && +etaDate ? getFormattedDate(etaDate) : null;

        let lateOrAtRiskTag: React.ReactNode = null;
        if (!appointmentNeeded && formatted != null && etaDate != null) {
          const etaMoment = moment(etaDate.toString(), "YYYYMMDD");
          const today = moment().startOf("day");
          if (etaMoment.isValid()) {
            if (etaMoment.isBefore(today)) {
              lateOrAtRiskTag = <Tag mods={{ color: TagColor.RED }}>Late</Tag>;
            } else if (etaMoment.isSame(today, "day")) {
              lateOrAtRiskTag = <Tag mods={{ color: TagColor.YELLOW }}>At Risk</Tag>;
            }
          }
        }

        const openCreateAppointment = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (onSchedule) onSchedule(row.probillNumber);
        };

        return (
          <div className="plan-probills-eta-cell">
            <div className="plan-probills-eta-cell__main">
              {formatted ?? "—"}
            </div>
            {appointmentNeeded && (
              <div className="plan-probills-eta-cell__tag">
                {canCreateAppointment ? (
                  <Tag mods={{ color: TagColor.RED, flex: true }} mix="plan-probills-eta-appt-needed-tag">
                    <button
                      type="button"
                      className="plan-probills-eta-cell__appt-needed-btn"
                      onClick={openCreateAppointment}
                      aria-label={`Create appointment for probill ${row.probillNumber}`}
                    >
                      <span>Appt. Needed</span>
                      <XGSIcon icon={XGSIcons.faExternalLinkAlt} />
                    </button>
                  </Tag>
                ) : (
                  <Tag mods={{ color: TagColor.RED, flex: true }} mix="plan-probills-eta-appt-needed-tag">
                    <span className="plan-probills-eta-cell__appt-needed-static">
                      Appt. Needed
                      <XGSIcon icon={XGSIcons.faExternalLinkAlt} />
                    </span>
                  </Tag>
                )}
              </div>
            )}
            {!appointmentNeeded && lateOrAtRiskTag != null && (
              <div className="plan-probills-eta-cell__tag">{lateOrAtRiskTag}</div>
            )}
          </div>
        );
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: getFormattedDate,
        filterSortFn: (date1: string, date2: string) => (moment(date1, "YYYYMMDD").isBefore(moment(date2, "YYYYMMDD"), "date") ? -1 : 1)
      },
      filterFn: "equals",
      size: 120,
    }),
    columnHelper.accessor("probillStatus", {
      id: "probillStatus",
      header: "Status",
      cell: (cellProps) => (
        <div className="plan-probills-status-cell">
          <Tag mods={{ color: TagColor.BLUE }}>
            <Link
              className="xgs-table-link-cell"
              to={`/shipments/${cellProps.row.original.probillNumber}`}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              {cellProps.getValue()}
            </Link>
          </Tag>
        </div>
      ),
      meta: {
        filterVariant: "select",
      },
      size: 130,
    }),
    columnHelper.accessor("tags", {
      id: "tags",
      header: "Accessorials",
      cell: (cellProps) => (
        getAccessorialIcons(
          cellProps.getValue(),
          cellProps.row.original.probillNumber,
          options?.onScheduleAppointment
        )
      ),
      enableColumnFilter: false,
      enableSorting: false,
      size: 150,
    }),
  ];

export default getPlanProbillsColumns;
